import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { useCoupons } from 'hooks/useFetchCoupons';
import { useUserStore } from 'stores/user';

export const RemainingSessions = (): React.ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const {
    remainingSessionsThisMonth,
    remainingSessions,
    couponsCount,
    enterpriseSessions,
    isLoading,
    coupons,
  } = useCoupons();
  const isInitialLoading = isLoading && !coupons;
  const patient = useUserStore(state => state.user);
  const activeCoupons = Object.entries(couponsCount);
  const hasCoupons = activeCoupons.length > 0;
  const isInfinite = remainingSessionsThisMonth === Infinity;
  const showPaeInfo = remainingSessionsThisMonth > 0 && !enterpriseSessions && !isInfinite;
  const showBreakdownList = showPaeInfo || hasCoupons;
  const showDetailsCard = (remainingSessions > 0 && !isInfinite) || hasCoupons;
  const hasAnyRemainingSessions = remainingSessions > 0 || isInfinite || enterpriseSessions > 0;

  const handleOnClick = (): void => {
    if (patient && !patient.profileCompleteToBookAppointments) {
      navigate(ROUTES.APPOINTMENTS_PERSONAL_DATA);
      return;
    }
    navigate(ROUTES.APPOINTMENTS_RESERVE);
  };

  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      {showDetailsCard && (
        <Card>
          <div className="flex flex-col gap-4">
            <div className="flex gap-2 items-center">
              <span className="text-5xl font-semibold text-teal-600">{remainingSessions}</span>
              <div className="flex flex-col gap-0">
                <p className="text-lg leading-tight font-semibold m-0">
                  {t('RemainingSessions.heading')}
                </p>
                <p className="text-xs font-medium text-gray-400">{t('RemainingSessions.body')}</p>
              </div>
            </div>
            {showBreakdownList && (
              <>
                <Separator />
                <div className="flex flex-col gap-2 text-sm text-gray-600">
                  {showPaeInfo && (
                    <span>
                      <span className="text-xl font-semibold text-denim-600 mr-1">
                        {remainingSessionsThisMonth}
                      </span>
                      {t('RemainingSessions.types.PAE')}
                    </span>
                  )}
                  {activeCoupons.map(([speciality, count]) => (
                    <span key={speciality}>
                      <span className="text-xl font-semibold text-denim-600 mr-1">{count}</span>
                      {t(`RemainingSessions.types.${speciality}`)}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      )}
      <Card
        className={cn(
          'am-lg:px-4 am-lg:py-3',
          hasAnyRemainingSessions ? 'bg-teal-100' : 'bg-red-100'
        )}
      >
        <div className="flex flex-col items-start justify-between gap-4 am-xl:flex-row am-xl:items-center">
          <p
            className={cn('text-sm font-semibold m-0', hasAnyRemainingSessions && 'text-teal-600')}
          >
            {t(
              hasAnyRemainingSessions
                ? 'RemainingSessions.Banner.body'
                : 'RemainingSessions.Banner.empty'
            )}
          </p>
          <Button onClick={handleOnClick} className="w-full am-xl:w-fit" variant="secondary">
            {t('AppointmentsReserve.cta')}
          </Button>
        </div>
      </Card>
    </>
  );
};
